import {
  Column,
  ColumnDef,
  TableState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { CSSProperties, useRef, useState } from 'react';
import { useWidthChange } from '../../hooks/width-change';
import './Table.scss';

interface TableProps<T = unknown> {
  isLoading: boolean;
  data: T[];
  columns: ColumnDef<T>[];
  state?: Partial<TableState>;
  tableClassName?: string;
  size?: number;
  onRowClick?: (row: T) => void;
}

const getIsLastLeftPinnedColumn = (isPinned, column) =>
  isPinned === 'left' && column.getIsLastColumn('left');

const getIsFirstRightPinnedColumn = (isPinned, column) =>
  isPinned === 'right' && column.getIsFirstColumn('right');

const getPinnedClassNames = (column) => {
  const isPinned = column.getIsPinned();
  if (getIsLastLeftPinnedColumn(isPinned, column)) return 'global_table-last-left-pinned';
  if (getIsFirstRightPinnedColumn(isPinned, column)) return 'global_table-first-right-pinned';
  return '';
};

const getCommonPinningStyles = (
  column: Column<unknown>,
  tableWidth: number,
  wrapperWidth: number
): CSSProperties => {
  const isPinned = tableWidth > wrapperWidth ? column.getIsPinned() : false;

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    maxWidth: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

const PAGE_SIZE = 4;

const TableRow = ({
  children,
  ...props
}: {
  children: React.ReactNode;
  'data-testid'?: string;
  onClick?: () => void;
}) => <tr {...props}>{children}</tr>;

const SkeletonTableRows = ({
  columnCol,
  size = PAGE_SIZE,
}: {
  columnCol: number;
  size: number;
}) => {
  return Array(size)
    .fill(0)
    .map((_, index) => (
      <TableRow key={index} data-testid={`skeleton-row-${index}`}>
        {Array(columnCol)
          .fill(0)
          .map((_, index) => (
            <td key={'col-' + index} className="skeleton">
              <div className="skeleton-line" />
            </td>
          ))}
      </TableRow>
    ));
};

const TableContent = ({
  table,
  isLoading,
  wrapperWidth,
  size = PAGE_SIZE,
  onRowClick,
}: {
  table: ReturnType<typeof useReactTable>;
  isLoading: boolean;
  wrapperWidth: number;
  size?: number;
  onRowClick?: (row: unknown) => void;
}) => {
  if (isLoading) {
    return <SkeletonTableRows size={size} columnCol={table.getVisibleFlatColumns().length} />;
  }

  if (table.getRowModel().rows.length === 0) {
    return (
      <TableRow>
        <td
          colSpan={table.getAllColumns().length}
          className="text-left text-color-primary-dark fst-italic"
        >
          No items found
        </td>
      </TableRow>
    );
  }

  const rowClickHandler = (data) => {
    if (onRowClick) {
      onRowClick(data);
    }
  };

  return table.getRowModel().rows.map((row) => (
    <TableRow key={row.id} onClick={() => rowClickHandler(row.original)}>
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          style={{ ...getCommonPinningStyles(cell.column, table.getTotalSize(), wrapperWidth) }}
          className={table.getTotalSize() > wrapperWidth ? getPinnedClassNames(cell.column) : ''}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </TableRow>
  ));
};

export const Table = ({
  columns,
  data,
  isLoading,
  state = {},
  tableClassName,
  size = PAGE_SIZE,
  onRowClick,
}: TableProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const wrapperWidth = useWidthChange(wrapperRef);
  const [isResizing, setIsResizing] = useState(false);

  const table = useReactTable({
    data,
    columns,
    state,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 30,
      size: 150,
      maxSize: 500,
    },
  });

  return (
    <div
      className={`global_table-responsive table-responsive ${isResizing ? 'table-resizing' : ''}`}
      ref={wrapperRef}
    >
      <table
        className={`table global_table align-middle mb-0 ${tableClassName}`}
        style={{
          minWidth: table.getTotalSize(),
          width: '100%',
        }}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  data-pinned={header.column.getIsPinned()}
                  style={{
                    ...getCommonPinningStyles(header.column, table.getTotalSize(), wrapperWidth),
                    width: header.getSize(),
                    position: 'relative',
                  }}
                  className={`
                    ${table.getTotalSize() > wrapperWidth ? getPinnedClassNames(header.column) : ''}
                  `}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanResize() && (
                    <button
                      type="button"
                      aria-label="Resize column"
                      className={`resizer ${isResizing ? 'isResizing' : ''}`}
                      onMouseDown={(e) => {
                        setIsResizing(true);
                        header.getResizeHandler()(e);
                      }}
                      onMouseUp={() => setIsResizing(false)}
                      onMouseLeave={() => setIsResizing(false)}
                      onTouchStart={(e) => {
                        setIsResizing(true);
                        header.getResizeHandler()(e);
                      }}
                      onTouchEnd={() => setIsResizing(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsResizing(true);
                          header.getResizeHandler()(e);
                        }
                      }}
                      style={{
                        ...(header.column.getIsPinned() === 'left'
                          ? { left: `${header.getSize()}px` }
                          : header.column.getIsPinned() === 'right'
                          ? { right: '0px' }
                          : {}),
                      }}
                    />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="table-group-divider">
          <TableContent
            size={size}
            table={table}
            isLoading={isLoading}
            wrapperWidth={wrapperWidth}
            onRowClick={onRowClick}
          />
        </tbody>
      </table>
    </div>
  );
};
